/**
 * Your own account, who else is in this kennel, and how somebody new gets in —
 * in that order, which is the order of how often each is wanted.
 *
 * The screen opens on the reader's own profile because that is what they came
 * for nine times in ten: the name against every weight they record, and the
 * language the app speaks. The list of people is below it, and the invitation
 * panel below that, folded shut.
 *
 * TWO THINGS ARE HIDDEN BEHIND A TAP, for the same reason. A role picker on
 * every row and the invitation form open at rest turned a screen that answers
 * "who is here" into two screens of controls nobody was using. Both are still
 * one tap away; neither is in the way of the sentence the reader wanted.
 *
 * NOBODY MAY EDIT THEIR OWN MEMBERSHIP HERE. An owner cannot change their own
 * role and cannot remove themselves — the controls are simply absent from their
 * own row. The last-owner rule still lives in `changeMemberRole` and still
 * refuses; this is the softer rule in front of it, so that the one account that
 * can fix everything else cannot be locked out by a mis-tap on a phone.
 */
import {
  canManageMembers,
  type Invite,
  type Member,
  type Role,
  ROLES,
  VIEW_SCOPES,
  type ViewScope,
} from '@puppy-doctor/platform';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import {
  createInvite,
  joinUrl,
  normalizeEmail,
  resendInvite,
  revokeInvite,
  useInvitesForTenant,
} from '@/api/invites.ts';
import { changeMemberRole, useMembers, useTenantDoc } from '@/api/tenant.ts';
import { Avatar } from '@/components/Avatar.tsx';
import { useConfirm } from '@/components/Confirm.tsx';
import { DisplayNameField } from '@/components/DisplayNameField.tsx';
import { InstallApp } from '@/components/InstallApp.tsx';
import { LanguagePicker } from '@/components/LanguagePicker.tsx';
import { RoleBadge } from '@/components/RoleBadge.tsx';
import { Button, Checkbox, Panel, Select, TextField } from '@/components/ui.tsx';
import { cn } from '@/lib/cn.ts';
import { expiryOf, useNow } from '@/lib/expiry.ts';
import { useProfile, useTenant } from '@/tenant/TenantProvider.tsx';
import i18n, { currentLocale } from '@/i18n/index.ts';

export const Route = createFileRoute('/members')({
  component: People,
});

const asDate = (millis: number): string =>
  new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(millis);

/**
 * The shared look of a `<details>` summary on this screen: a 44px row that has
 * lost the native triangle in both engines, so the chevron beside it is the only
 * one — and that one turns over with the disclosure.
 */
const SUMMARY =
  'flex min-h-tap cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden ' +
  // Both callers pull the row a hair wider than its text with `-mx-1 px-1`, so
  // the hovered fill reads as a row rather than as a highlight applied to a
  // sentence.
  'rounded-lg transition-colors hover:bg-sunken';

function People() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenantId, role } = useTenant();

  if (tenantId === null) return null;
  const isOwner = role !== null && canManageMembers(role);

  return (
    <div className="flex flex-col gap-6">
      <Account />

      <section>
        {/* Tight to the list under it: the heading and the first name are one
            thing, and a gap the size of the one between sections says they are
            two. */}
        <h2 className="mb-1 text-lg font-bold tracking-[-0.01em]">{t('member.title')}</h2>
        <MemberList tenantId={tenantId} myUid={user?.uid ?? ''} mayManage={isOwner} />
      </section>

      {isOwner ? (
        <OwnerPanel tenantId={tenantId} myUid={user?.uid ?? ''} myEmail={user?.email ?? ''} />
      ) : (
        <p className="text-sm text-ink-faint">{t('member.ownerOnly')}</p>
      )}

      <SignOut />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Your own account, at the top of the one screen that is already about people.
 *
 * There is no settings route, and adding one for two preferences would be a tab
 * in the navigation for something a person touches twice in the life of the app.
 * This is where sign-out already lived, which is where anyone looking for "my
 * account" looks first.
 */
function Account() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { name, email } = useProfile();
  const { role } = useTenant();

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold tracking-[-0.02em]">{t('member.accountTitle')}</h1>

      <div className="flex items-center gap-3">
        {/* The role beside the avatar rather than the name, which is the line
            above it: the name answers "who is signed in", and on this screen —
            the one place where roles are handed out — the role answers the
            question the reader came with. */}
        <Avatar name={name} email={user?.email ?? null} className="size-11" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{name}</p>
          <p className="truncate text-sm text-ink-faint">
            {role !== null && (
              <>
                {t(`role.${role}`)}
                <span aria-hidden> · </span>
              </>
            )}
            {t('auth.signedInAs', { email })}
          </p>
        </div>
      </div>

      <DisplayNameField />

      <LanguagePicker />

      {/* Last in the block, and nothing is displaced when it renders nothing —
          which is most of the time: it draws only on a phone that can still be
          given the icon. See components/InstallApp.tsx. */}
      <InstallApp />
    </div>
  );
}

/**
 * Last on the screen, below the invitations, because leaving is the one thing
 * here nobody is looking for — and a `div` rather than a `<footer>`: the shell
 * in __root.tsx renders the page's one footer landmark, and a second one makes
 * both useless to a screen reader.
 */
function SignOut() {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <div className="mt-2 border-t border-line pt-5">
      <Button variant="quiet" className="-mx-2 px-2 text-sm" onClick={() => void signOut()}>
        {t('auth.signOut')}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * A ruled list rather than a stack of cards: these rows are scanned down a
 * column for one name, and a border round each of them only adds six lines of
 * chrome between the reader and the thing they are looking for.
 *
 * Two lines and a badge per person, and nothing else at rest. The role picker
 * that used to sit open on every row is behind "Change role" — at rest the role
 * is a thing to READ, which is what the badge was always for.
 */
function MemberList({
  tenantId,
  myUid,
  mayManage,
}: {
  tenantId: string;
  myUid: string;
  /** An owner gets the controls; everybody else reads the list. */
  mayManage: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useMembers(tenantId);

  const members = data ?? [];
  if (members.length === 0) return <p className="text-ink-faint">{t('member.empty')}</p>;

  return (
    <ul className="divide-y divide-line">
      {members.map((member: Member) => (
        <MemberRow
          key={member.uid}
          tenantId={tenantId}
          member={member}
          isMe={member.uid === myUid}
          mayManage={mayManage}
        />
      ))}
    </ul>
  );
}

function MemberRow({
  tenantId,
  member,
  isMe,
  mayManage,
}: {
  tenantId: string;
  member: Member;
  isMe: boolean;
  mayManage: boolean;
}) {
  const { t } = useTranslation();
  const { name: myName } = useProfile();
  const [editing, setEditing] = useState(false);

  /**
   * Your own row is drawn from your profile, not from the membership. The
   * membership carries a copy of the name as it was on the day the function
   * wrote it, and `members/{uid}` is a document no client may write — so
   * renaming yourself here would otherwise leave you reading a stale name in the
   * one list where you would look to check the rename worked. Everyone else's
   * row is the copy, because nobody may read anybody else's profile.
   */
  const name = isMe ? myName : member.displayName;

  // Never on your own row. See the note at the top of the file.
  const mayEdit = mayManage && !isMe;

  return (
    <li className="flex flex-col gap-2 py-2.5">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{name}</p>
          <p className="truncate text-xs text-ink-faint">
            {isMe ? t('member.you') : t('member.since', { date: asDate(member.joinedAt) })}
          </p>
        </div>
        <RoleBadge role={member.role} />
        {mayEdit && (
          <Button
            variant="quiet"
            aria-expanded={editing}
            className="min-h-tap shrink-0 px-2 text-sm font-semibold text-brand-ink"
            onClick={() => setEditing((open) => !open)}
          >
            {editing ? t('member.changeRoleDone') : t('member.changeRole')}
          </Button>
        )}
      </div>

      {mayEdit && editing && <MemberControls tenantId={tenantId} member={member} name={name} />}
    </li>
  );
}

/**
 * The things an owner can do to somebody else's membership, revealed together
 * because they are the same decision at different strengths.
 *
 * A `Select` for the role rather than the segmented control the invite panel
 * uses: this is a correction rather than a decision being weighed — the invite
 * panel is where the roles are explained, and by the time somebody is in the
 * list the reader knows what they are choosing between.
 *
 * WHAT AN OBSERVER IS SHOWN IS EDITABLE HERE TOO, and it has to be: the choice
 * is made when the invitation is sent, and without this the only way to give a
 * puppy's family the weights as well as the photographs would be to remove them
 * and invite them again. The boxes appear under the role only while the row IS
 * an observer, because for every other role there is nothing to choose.
 *
 * Each box writes immediately, the way the role select does — there is no Save
 * on this panel and adding one for half of it would be two idioms in one card.
 * The last box cannot be unticked: an observer who is shown nothing is the state
 * `ScopePicker` refuses at the other end, and here there is no Invite button to
 * refuse on.
 *
 * The removal is behind the shared confirmation, and it names the person: a
 * mis-tap here takes away somebody's access to a kennel they may have been
 * following for weeks.
 */
function MemberControls({
  tenantId,
  member,
  name,
}: {
  tenantId: string;
  member: Member;
  name: string;
}) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (role: Role | null, scopes: readonly ViewScope[] = member.scopes) => {
    setError(null);
    setBusy(true);
    changeMemberRole(tenantId, member.uid, role, scopes)
      // The list is fed by a listener, so a success needs nothing here: the row
      // redraws itself. A failure is the whole reason this is awaited — the
      // last-owner rule lives in the function and nowhere else.
      .catch(() => setError(t('member.changeFailed')))
      .finally(() => setBusy(false));
  };

  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-sunken px-3 py-2.5">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Select
            label={t('member.roleLabel')}
            value={member.role}
            disabled={busy}
            onChange={(event) => {
              const next = ROLES.find((option) => option === event.target.value);
              if (next !== undefined && next !== member.role) apply(next);
            }}
          >
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {t(`role.${option}`)}
              </option>
            ))}
          </Select>
        </div>

        <Button
          variant="quiet"
          className="min-h-tap shrink-0 px-2 text-sm text-alert"
          disabled={busy}
          onClick={() =>
            confirm({
              title: t('member.removeTitle', { name }),
              body: t('member.removeBody', { name }),
              confirmLabel: t('member.remove'),
              onConfirm: () => apply(null),
            })
          }
        >
          {t('member.remove')}
        </Button>
      </div>

      {member.role === 'viewer' && (
        <div className="border-t border-line pt-1">
          <p className="pt-1 text-xs font-semibold tracking-wide text-ink-faint uppercase">
            {t('invite.scopesLabel')}
          </p>
          {VIEW_SCOPES.map((scope) => {
            const on = member.scopes.includes(scope);
            return (
              <Checkbox
                key={scope}
                label={t(`scope.${scope}`)}
                checked={on}
                onChange={(next) => {
                  // Unticking the last one would leave somebody signed in with
                  // every screen empty. The box simply does not move.
                  if (!next && member.scopes.length === 1) return;
                  apply(
                    member.role,
                    VIEW_SCOPES.filter((one) =>
                      one === scope ? next : member.scopes.includes(one),
                    ),
                  );
                }}
              />
            );
          })}
        </div>
      )}

      {error !== null && (
        <p role="alert" className="text-sm text-alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Mounted only for an owner, which is what keeps the invite listener off every
 * other member's phone. The rules would let any member read this collection —
 * seeing who else has been invited is not a leak — but a subscription nobody
 * looks at is billed reads for the life of the tab.
 *
 * The form is folded shut. Inviting somebody is a thing that happens a handful
 * of times in the life of a kennel; the pending list under it is the thing an
 * owner comes back to check, and it was being pushed off the screen by four role
 * descriptions written for the one evening they are read.
 */
function OwnerPanel({
  tenantId,
  myUid,
  myEmail,
}: {
  tenantId: string;
  myUid: string;
  myEmail: string;
}) {
  const { t } = useTranslation();
  const { data: tenant } = useTenantDoc(tenantId);
  const { data } = useInvitesForTenant(tenantId);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('handler');
  /**
   * What an observer will be shown, kept while another role is selected.
   *
   * Both to begin with, which is the invitation somebody sends when they have
   * not thought about it — and the same thing every membership written before
   * scopes existed means. Untouched by the role radio, so flicking through the
   * three roles and coming back does not silently reset a pair of boxes the
   * owner had already set.
   */
  const [scopes, setScopes] = useState<readonly ViewScope[]>(VIEW_SCOPES);
  const [error, setError] = useState<string | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);

  const pending = (data ?? []).filter((invite: Invite) => invite.acceptedAt === null);

  const send = () => {
    const address = normalizeEmail(email);
    if (address === '') {
      setError(t('invite.needsEmail'));
      return;
    }
    if (address === normalizeEmail(myEmail)) {
      setError(t('invite.selfEmail'));
      return;
    }
    // An observer who is shown nothing is not an observer. See `ScopePicker`.
    if (role === 'viewer' && scopes.length === 0) {
      setError(null);
      setScopeError(t('invite.needsScope'));
      return;
    }

    setError(null);
    setScopeError(null);
    createInvite({
      tenantId,
      tenantName: tenant?.name ?? '',
      email: address,
      role,
      // Only an observer is scoped; every other role is in the kennel to run it
      // and sees all of it. Written out in full either way, so the document says
      // what it means without anybody having to know the role to read it.
      scopes: role === 'viewer' ? scopes : VIEW_SCOPES,
      invitedBy: myUid,
      // The language this owner is reading, which is the best guess anyone has
      // at the language the person being invited reads. See `Invite.locale`.
      locale: currentLocale(),
    });
    // Not awaited, and nothing to wait for: the listener puts the new row in
    // the list below on the next frame, offline included. See api/invites.ts.
    setEmail('');
    setScopes(VIEW_SCOPES);
  };

  return (
    <Panel className="flex flex-col gap-3">
      <h2 className="text-lg font-bold tracking-[-0.01em]">{t('invite.panelTitle')}</h2>

      <details className="group -mx-1">
        <summary className={cn(SUMMARY, 'px-1')}>
          <span className="flex-1 text-sm font-semibold text-brand-ink">{t('invite.add')}</span>
          <ChevronDown
            aria-hidden
            className="size-4 shrink-0 text-ink-faint transition-transform group-open:rotate-180"
          />
        </summary>

        <div className="flex flex-col gap-5 px-1 pt-2 pb-1">
          <TextField
            label={t('invite.emailLabel')}
            placeholder={t('invite.emailPlaceholder')}
            type="email"
            inputMode="email"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={error ?? undefined}
            hint={t('invite.hint')}
          />

          <RolePicker
            role={role}
            scopes={scopes}
            error={scopeError}
            onChange={(next) => {
              setRole(next);
              setScopeError(null);
            }}
            onScopes={(next) => {
              setScopes(next);
              setScopeError(null);
            }}
          />

          <Button full onClick={send}>
            {t('invite.send')}
          </Button>
        </div>
      </details>

      {pending.length === 0 ? (
        <p className="text-sm text-ink-faint">{t('invite.none')}</p>
      ) : (
        <ul className="divide-y divide-line border-t border-line">
          {pending.map((invite) => (
            <PendingRow key={invite.id} invite={invite} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * One person who has been invited and has not arrived, as one line.
 *
 * WHAT STAYS ON THE CLOSED ROW IS THE FAILURE. An invitation is a document plus
 * an email, and the email is the half that can silently fail; an owner who
 * cannot see "not sent" assumes it arrived and waits a week for a vet who was
 * never told. So a failed send says so at rest, and everything that is merely
 * true of a healthy invitation — when it was sent, when it expires, what can be
 * done about it — is one tap down.
 *
 * Three actions, in the order they are needed. **Resend** for a mail that failed
 * or was never opened — it goes through a Cloud Function, so unlike everything
 * else in this app it is awaited and can report failure. **Copy link** for when
 * mail is the problem rather than the invitation: the address works on its own,
 * and reading it out over the phone or pasting it into a text message is a
 * perfectly good delivery mechanism. **Withdraw**, behind the shared
 * confirmation.
 */
function PendingRow({ invite }: { invite: Invite }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { expired, daysLeft } = expiryOf(invite.expiresAt, useNow());

  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  const resend = () => {
    setFailed(false);
    setSending(true);
    resendInvite(invite.id)
      .catch(() => setFailed(true))
      // The row stays mounted either way — the listener updates `sentAt` from
      // underneath it, which is what actually confirms the send.
      .finally(() => setSending(false));
  };

  const copy = () => {
    // `writeText` rejects on an insecure origin and in a browser whose clipboard
    // permission was refused, and neither is worth an error message: the address
    // is on screen in the failure case anyway.
    navigator.clipboard
      .writeText(joinUrl(invite.id))
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  const undelivered = invite.sendError !== null;

  return (
    <li>
      <details className="group py-1">
        <summary className={cn(SUMMARY, '-mx-1 items-start px-1 py-1.5')}>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{invite.email}</span>
            {/* `title` carries the server's own words, which are useful to
                whoever debugs it and noise to everyone else. */}
            {undelivered && (
              <span className="block text-xs text-alert" title={invite.sendError ?? undefined}>
                {t('invite.sendFailedShort')}
              </span>
            )}
          </span>
          <RoleBadge role={invite.role} />
          <ChevronDown
            aria-hidden
            className="size-4 shrink-0 text-ink-faint transition-transform group-open:rotate-180"
          />
        </summary>

        <div className="flex flex-col gap-1.5 pb-2">
          <p className="text-sm text-ink-faint">
            {expired ? t('invite.expired') : t('invite.expiresIn', { count: daysLeft })}
          </p>

          {/* What became of the email, in full. */}
          <p
            className={cn('text-sm', undelivered ? 'text-alert' : 'text-ink-faint')}
            title={invite.sendError ?? undefined}
          >
            {undelivered
              ? t('invite.sendFailedStatus')
              : invite.sentAt !== null
                ? t('invite.sentAt', { date: asDate(invite.sentAt) })
                : t('invite.sending')}
          </p>

          <div className="flex flex-wrap items-center gap-1">
            <Button
              variant="quiet"
              className="min-h-tap px-2 text-sm font-semibold text-brand-ink"
              disabled={sending}
              onClick={resend}
            >
              {sending ? t('invite.sending') : t('invite.resend')}
            </Button>
            <Button variant="quiet" className="min-h-tap px-2 text-sm" onClick={copy}>
              {copied ? t('invite.linkCopied') : t('invite.copyLink')}
            </Button>
            <Button
              variant="quiet"
              aria-label={t('invite.revoke')}
              className="ml-auto min-h-tap px-2 text-sm"
              onClick={() =>
                confirm({
                  title: t('invite.revokeTitle'),
                  body: t('invite.revokeBody', { email: invite.email }),
                  confirmLabel: t('invite.revoke'),
                  onConfirm: () => revokeInvite(invite.id),
                })
              }
            >
              {t('invite.revokeShort')}
            </Button>
          </div>

          {failed && (
            <p role="alert" className="text-sm text-alert">
              {t('invite.resendFailed')}
            </p>
          )}
        </div>
      </details>
    </li>
  );
}

/**
 * The three roles with what each one may do, because "observer" is not
 * self-explanatory to the person choosing between them at eleven at night.
 *
 * Choosing the observer opens the list of what they will be shown, INSIDE the
 * same option, indented under the sentence that describes the role. It is not a
 * second question below the group: it is part of what "observer" means here, and
 * a separate field would sit there greyed out for the two roles it does not
 * apply to.
 */
function RolePicker({
  role,
  scopes,
  error,
  onChange,
  onScopes,
}: {
  role: Role;
  scopes: readonly ViewScope[];
  /** Shown under the list when nothing is ticked and Invite was pressed. */
  error: string | null;
  onChange: (role: Role) => void;
  onScopes: (scopes: readonly ViewScope[]) => void;
}) {
  const { t } = useTranslation();

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-2 text-sm font-medium text-ink-soft">{t('invite.roleLabel')}</legend>
      {ROLES.map((option) => (
        // A `div` around the label rather than the label itself being the card,
        // because the observer's card holds checkboxes — and a `<label>` inside
        // a `<label>` is markup where a tap on the inner box also toggles the
        // outer radio. `has-checked:` still reads the radio from out here: it
        // compiles to `:has(:checked)`, which does not care how deep it is.
        <div
          key={option}
          // `not-has-checked:`, as in LanguagePicker: the chosen role is drawn
          // with `has-checked:` on the same two properties, and a bare `hover:`
          // would be a coin toss between them.
          className={cn(
            'rounded-xl border border-line px-3 py-2 transition-colors',
            'not-has-checked:hover:border-line-strong not-has-checked:hover:bg-sunken',
            'has-checked:border-brand-600 has-checked:bg-brand-50',
          )}
        >
          <label className="flex min-h-tap cursor-pointer items-center gap-3">
            <input
              type="radio"
              name="role"
              value={option}
              checked={role === option}
              onChange={() => onChange(option)}
              className="size-5 shrink-0 accent-brand-600"
            />
            <span className="min-w-0">
              <span className="block font-medium">{t(`role.${option}`)}</span>
              <span className="block text-sm text-ink-faint">{t(`role.${option}What`)}</span>
            </span>
          </label>

          {option === 'viewer' && role === 'viewer' && (
            <ScopePicker scopes={scopes} error={error} onChange={onScopes} />
          )}
        </div>
      ))}
    </fieldset>
  );
}

/**
 * What an observer will be shown, one box per part of the record.
 *
 * AT LEAST ONE, and the form refuses to send without it — an observer with
 * nothing ticked is an account that can sign in, read a litter's name and find
 * every screen under it empty, which is not an invitation anybody means to send.
 * The refusal is on the Invite button rather than on the boxes themselves,
 * because unticking the last one is a legitimate step on the way to ticking the
 * other, and a box that fights back mid-thought is worse than a sentence at the
 * end.
 *
 * Indented under the radio it belongs to, so it reads as part of what "observer"
 * means here rather than as a fourth question below the group — and it is drawn
 * only while the observer is the chosen role, because for the other two there is
 * nothing to choose. Each box carries its own label, which is the part that has
 * to be right.
 */
function ScopePicker({
  scopes,
  error,
  onChange,
}: {
  scopes: readonly ViewScope[];
  error: string | null;
  onChange: (scopes: readonly ViewScope[]) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mt-1 border-t border-line pt-1 pl-8">
      <p className="pt-1 text-xs font-semibold tracking-wide text-ink-faint uppercase">
        {t('invite.scopesLabel')}
      </p>
      {VIEW_SCOPES.map((scope) => (
        <Checkbox
          key={scope}
          label={t(`scope.${scope}`)}
          checked={scopes.includes(scope)}
          // Rebuilt from `VIEW_SCOPES` rather than appended to, so the stored
          // order is always the declared one — two memberships with the same two
          // scopes in different orders are the same membership, and a reader
          // comparing them should not have to know that.
          onChange={(on) =>
            onChange(
              VIEW_SCOPES.filter((one) =>
                one === scope ? on : scopes.includes(one),
              ),
            )
          }
        />
      ))}
      {error !== null && (
        <p role="alert" className="pb-1 text-sm text-alert">
          {error}
        </p>
      )}
    </div>
  );
}
